/**
 * MealPlanContext - Provides core meal plan and profile data
 *
 * This context provides the main plan data, user/profile information,
 * and related state that components need for display and logic.
 */

import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

// Create the context
const MealPlanContext = createContext({
    plan: null,
    user: null,
    patient: null,
    profile: null,
    mealTypes: [],
    maxOffset: 0,
    leftoversEnabled: false,
    editableParticipants: true,
    synced: true,
    // Nutrition data for modals
    analysis: null,
    groceries: [], // Array of grocery objects
});

// Custom hook for consuming meal plan data
export const useMealPlanData = () => {
    const context = useContext(MealPlanContext);
    if (!context) {
        throw new Error('useMealPlanData must be used within MealPlanProvider');
    }
    return context;
};

// Provider component
export const MealPlanProvider = ({
    children,
    plan,
    user,
    patient,
    profile,
    mealTypes = [],
    maxOffset = 0,
    editableParticipants = true,
    synced = true,
    analysis = null,
    groceries = [],
}) => {
    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        plan,
        user,
        patient,
        profile,
        mealTypes,
        maxOffset,
        leftoversEnabled: profile?.preferences?.leftovers_enabled || false,
        editableParticipants,
        synced,
        analysis,
        groceries,
    }), [plan, user, patient, profile, mealTypes, maxOffset, editableParticipants, synced, analysis, groceries]);

    return (
        <MealPlanContext.Provider value={value}>
            {children}
        </MealPlanContext.Provider>
    );
};

MealPlanProvider.propTypes = {
    children: PropTypes.node.isRequired,
    plan: PropTypes.object,
    user: PropTypes.object,
    patient: PropTypes.object,
    profile: PropTypes.object,
    mealTypes: PropTypes.array,
    maxOffset: PropTypes.number,
    editableParticipants: PropTypes.bool,
    synced: PropTypes.bool,
    analysis: PropTypes.object,
    groceries: PropTypes.array, // Array of grocery objects
};

export default MealPlanContext;
